import { createSelector } from '@reduxjs/toolkit';

import { getDiagramSnapshotSizes } from 'src/containers/ProjectDiagram/utils/isLargeDiagramSnapshot';
import { memoizeSelectorFactory } from 'src/selectors/memoizeSelectorFactory';
import { projectSelector } from 'src/selectors/project';
import {
  getPendingModelDataSizes,
  isLargeModelSize,
} from 'src/utils/checkImportModelLimits';

export const diagramSnapshotSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      projectSelector(projectId),
      (project) => project?.diagram || null
    )
);

// Memoized so the class/property counting only reruns when the snapshot
// changes, not on every render
export const diagramModelSizesSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(diagramSnapshotSelector(projectId), (diagram) =>
      getDiagramSnapshotSizes(diagram?.model)
    )
);

// An imported model's `classes`/`properties` are populated on
// ADDED_PENDING_MODEL_DATA_TO_PROJECT and then kept forever — nothing nulls them
// once the data reaches the diagram — and `modelsById` also holds models the user
// has deleted (see ProjectsState.modelsById). Counting it wholesale would pin a
// project to "large" for good, even after the classes are pruned or the whole
// model is deleted. So count a model only while it is genuinely mid-import:
// its data has landed in Redux but no diagram commit has claimed it yet.
const uncommittedModelSizesSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      projectSelector(projectId),
      diagramSnapshotSelector(projectId),
      (project, diagram) => {
        if (!project || !diagram) {
          return getPendingModelDataSizes({});
        }

        const { deletedModelIds, modelsByIri } = diagram.model.modelData;
        // A model id appears in modelsByIri as soon as
        // addProjectModelsToDiagramModel commits it, so this is the handoff
        // point: past it, diagramModelSizesSelector owns the count (and can
        // shrink again as the user edits).
        const committedModelIds = new Set(Object.values(modelsByIri).flat());

        return getPendingModelDataSizes(
          Object.fromEntries(
            Object.entries(project.modelsById).filter(
              ([modelId]) =>
                !committedModelIds.has(modelId) &&
                !deletedModelIds?.includes(modelId)
            )
          )
        );
      }
    )
);

export const isLargeModelSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      diagramModelSizesSelector(projectId),
      uncommittedModelSizesSelector(projectId),
      (diagramSizes, uncommittedSizes) =>
        isLargeModelSize(diagramSizes) || isLargeModelSize(uncommittedSizes)
    )
);

export const isDraftDiagramSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(diagramSnapshotSelector(projectId), (diagram) =>
      Boolean(diagram?.model.modelData.draftData)
    )
);
